import type {
    FilterType,
    FilterValueByType,
    NullableFilterValues,
    NumberFilter,
} from './types.ts';
import {DateFilter} from "../dates";

function dateRuleKey(filter: DateFilter | null): string {
    if (!filter) {
        return 'null';
    }
    if (filter.preset) {
        return `preset:${filter.preset}`;
    }
    if (filter.rule) {
        const anchor =
            filter.rule.anchor?.type === 'date'
                ? filter.rule.anchor.value.toISO()
                : (filter.rule.anchor?.type ?? 'none');
        if (filter.rule.kind === 'open') {
            return `rule:open:${filter.rule.direction}:${filter.rule.amount}:${filter.rule.unit}:${anchor}`;
        }
        return `rule:bounded:${filter.rule.direction}:${filter.rule.amount ?? 'na'}:${filter.rule.unit}:${anchor}`;
    }
    return `range:${filter.start?.toISO() ?? 'null'}:${filter.end?.toISO() ?? 'null'}`;
}

function normalizeStringArray(value: string[] | null): string {
    return (value ?? []).slice().sort().join(',');
}

function numberFilterKey(value: NumberFilter | null): string {
    if (!value) return '';
    if (value.op === 'range') {
        return `num:range:${value.min ?? 'na'}:${value.max ?? 'na'}`;
    }
    return `num:${value.op}:${value.value ?? 'na'}`;
}

export function filterValueKey<TType extends FilterType>(
    type: TType,
    value: FilterValueByType<TType>,
): string {
    switch (type) {
        case 'string':
            return (value as FilterValueByType<'string'>) ?? '';
        case 'string[]':
            return normalizeStringArray(value as FilterValueByType<'string[]'>);
        case 'number':
            return numberFilterKey(value as FilterValueByType<'number'>);
        case 'boolean': {
            const bool = value as FilterValueByType<'boolean'>;
            return bool === null ? '' : (bool ? 'true' : 'false');
        }
        case 'DateFilter':
            return dateRuleKey(value as FilterValueByType<'DateFilter'> | null);
    }
}

export function filterValueEquals<TType extends FilterType>(
    type: TType,
    a: FilterValueByType<TType>,
    b: FilterValueByType<TType>,
): boolean {
    return filterValueKey(type, a) === filterValueKey(type, b);
}

function isDateFilterValue(value: unknown): value is DateFilter | null {
    if (value === null) {
        return true;
    }
    if (!value || typeof value !== 'object') {
        return false;
    }
    return 'start' in value && 'end' in value;
}

function isStringArrayValue(value: unknown): value is string[] | null {
    return (
        value === null ||
        (Array.isArray(value) && value.every((item) => typeof item === 'string'))
    );
}

function isStringValue(value: unknown): value is string | null {
    return value === null || typeof value === 'string';
}

function isBooleanValue(value: unknown): value is boolean | null {
    return value === null || typeof value === 'boolean';
}

function isNumberFilterValue(value: unknown): value is NumberFilter | null {
    if (value === null) return true;
    if (!value || typeof value !== 'object') return false;
    return 'op' in value;
}

function areValuesEqual(a: unknown, b: unknown): boolean {
    if (isStringValue(a) && isStringValue(b)) {
        return (a ?? '') === (b ?? '');
    }
    if (isStringArrayValue(a) && isStringArrayValue(b)) {
        return normalizeStringArray(a) === normalizeStringArray(b);
    }
    if (isBooleanValue(a) && isBooleanValue(b)) {
        return (a === null ? '' : String(a)) === (b === null ? '' : String(b));
    }
    if (isNumberFilterValue(a) && isNumberFilterValue(b)) {
        return numberFilterKey(a) === numberFilterKey(b);
    }
    if (isDateFilterValue(a) && isDateFilterValue(b)) {
        return dateRuleKey(a) === dateRuleKey(b);
    }
    return false;
}

export function filterValuesEqual<TFilters extends Record<string, unknown>>(
    a: NullableFilterValues<TFilters>,
    b: NullableFilterValues<TFilters>,
): boolean {
    const keys = new Set<string>([
        ...Object.keys(a as Record<string, unknown>),
        ...Object.keys(b as Record<string, unknown>),
    ]);

    for (const key of keys) {
        if (
            !areValuesEqual(
                (a as Record<string, unknown>)[key],
                (b as Record<string, unknown>)[key],
            )
        ) {
            return false;
        }
    }

    return true;
}
